import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CreateInNoteRequest,
    CreateOutNoteRequest,
    CreateWithdrawalNoteRequest,
    ListLotsParams,
    ListNotesParams,
    ListWithdrawalNotesParams,
    UpdateInNoteInfoRequest,
    UpdateInNoteRequest,
    UpdateOutNoteRequest,
} from '@/lib/api/scenting-lot-api'
import { scentingLotApi } from '@/lib/api/scenting-lot-api'
import { sortingLotKeys } from '@/hooks/use-sorting-lot-query'
import { scentingLogKeys } from '@/hooks/use-scenting-log-query'
import { m } from '@/lib/paraglide/messages'

// ============================================================================
// Query keys
// ============================================================================

export const scentingLotKeys = {
    all: ['scenting-lots'] as const,
    lots: () => [...scentingLotKeys.all, 'lots'] as const,
    lotList: (params: ListLotsParams) =>
        [...scentingLotKeys.lots(), 'list', params] as const,
    lotDetail: (id: string) =>
        [...scentingLotKeys.lots(), 'detail', id] as const,

    inNotes: () => [...scentingLotKeys.all, 'in-notes'] as const,
    inNoteList: (params: ListNotesParams) =>
        [...scentingLotKeys.inNotes(), 'list', params] as const,
    inNoteDetail: (id: string) =>
        [...scentingLotKeys.inNotes(), 'detail', id] as const,

    outNotes: () => [...scentingLotKeys.all, 'out-notes'] as const,
    outNoteList: (params: ListNotesParams) =>
        [...scentingLotKeys.outNotes(), 'list', params] as const,
    outNoteDetail: (id: string) =>
        [...scentingLotKeys.outNotes(), 'detail', id] as const,
    outNotesForPackaging: (search: string) =>
        [...scentingLotKeys.outNotes(), 'for-packaging', search] as const,

    withdrawalNotes: () =>
        [...scentingLotKeys.all, 'withdrawal-notes'] as const,
    withdrawalNoteList: (params: ListWithdrawalNotesParams) =>
        [...scentingLotKeys.withdrawalNotes(), 'list', params] as const,
    withdrawalNoteDetail: (id: string) =>
        [...scentingLotKeys.withdrawalNotes(), 'detail', id] as const,
}

// ============================================================================
// Lot queries
// ============================================================================

export function useScentingLots(
    params: ListLotsParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: scentingLotKeys.lotList(params),
        queryFn: () => scentingLotApi.listLots(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useScentingLotsInfinite(
    params: Omit<ListLotsParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...scentingLotKeys.lots(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            scentingLotApi.listLots({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useScentingLot(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: scentingLotKeys.lotDetail(id ?? ''),
        queryFn: () => scentingLotApi.getLot(id as string),
    })
}

/** Nhiều lô theo id, dùng chung cache chi tiết với useScentingLot. Cho ô chọn lô ở màn
 *  SỬA phiếu: lô của phiếu (seed) nằm ngoài trang đầu thì chưa có số dư — tra
 *  đúng các lô đó thay vì nạp cả danh sách. Trả lô đã tải xong. */
export function useScentingLotsByIds(ids: readonly string[]) {
    return useQueries({
        queries: ids.map((id) => ({
            queryKey: scentingLotKeys.lotDetail(id),
            queryFn: () => scentingLotApi.getLot(id),
        })),
        combine: (results) =>
            results.flatMap((r) => (r.data?.data ? [r.data.data] : [])),
    })
}

export function useScentingLotHistory(id: string | undefined) {
    return useInfiniteQuery({
        enabled: !!id,
        queryKey: [...scentingLotKeys.lots(), 'history', id ?? ''] as const,
        queryFn: ({ pageParam }) =>
            scentingLotApi.getLotHistory(id as string, {
                page: pageParam,
                per_page: 20,
            }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

// ============================================================================
// Stock-in note queries
// ============================================================================

export function useScentingInNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: scentingLotKeys.inNoteList(params),
        queryFn: () => scentingLotApi.listInNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useScentingInNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...scentingLotKeys.inNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            scentingLotApi.listInNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useScentingInNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: scentingLotKeys.inNoteDetail(id ?? ''),
        queryFn: () => scentingLotApi.getInNote(id as string),
    })
}

export function useCreateScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateInNoteRequest) =>
            scentingLotApi.createInNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            // Tạo PNK có thể trừ số dư phiếu lấy hàng kho phân loại (gợi ý) và
            // đóng nó khi cạn → làm mới list/detail phiếu lấy hàng để picker
            // phiếu sau không chọn lại note đã hết.
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            // Phiếu có thể vừa sinh ra từ một dòng sổ lấy hương (#M-16). Không
            // làm mới sổ thì trong 30 giây staleTime dòng đó vẫn hiện nút "Tạo
            // phiếu" — bấm tiếp là đẻ phiếu thứ hai cho cùng một mẻ.
            qc.invalidateQueries({ queryKey: scentingLogKeys.all })
            toast.success(m.scl_toast_create_pnk())
        },
    })
}

/**
 * Sửa ghi chú + ngày lấy hương của PNK đã duyệt. Chỉ cần làm mới chi tiết và danh
 * sách phiếu: hai trường này không đụng tồn kho lô, cũng không đụng số dư phiếu
 * lấy hàng nên không invalidate các query đó.
 */
export function useUpdateScentingInNoteInfo() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            req,
        }: {
            id: string
            req: UpdateInNoteInfoRequest
        }) => scentingLotApi.updateInNoteInfo(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            toast.success(m.scl_toast_update_in_info())
        },
    })
}

export function useUpdateScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateInNoteRequest }) =>
            scentingLotApi.updateInNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            // Sửa phiếu re-đồng bộ số dư phiếu lấy hàng phân loại (hoàn cũ + trừ mới).
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            toast.success(m.scl_toast_update_pnk())
        },
    })
}

export function useApproveScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.approveInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            toast.success(m.scl_toast_approve_pnk())
        },
    })
}

export function useRejectScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            scentingLotApi.rejectInNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            // Từ chối có thể hoàn số dư / mở lại phiếu lấy hàng phân loại.
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            toast.success(m.scl_toast_reject_pnk())
        },
    })
}

export function useCancelScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.cancelInNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            // Huỷ có thể hoàn số dư / mở lại phiếu lấy hàng phân loại.
            qc.invalidateQueries({
                queryKey: sortingLotKeys.withdrawalNotes(),
            })
            toast.success(m.scl_toast_cancel_pnk())
        },
    })
}

/** Rollback PNK đã duyệt: BE sinh PXK điều chỉnh trừ lại tồn (M-17). */
export function useRollbackScentingInNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.rollbackInNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNoteDetail(id) })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

// ============================================================================
// Stock-out note queries
// ============================================================================

export function useScentingOutNotes(
    params: ListNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: scentingLotKeys.outNoteList(params),
        queryFn: () => scentingLotApi.listOutNotes(params),
    })
}

/**
 * Infinite variant cho mobile "Xem thêm" pattern. Accumulate pages 1..N.
 * `enabled` false-safe: khi caller pass false (vd viewport không phải mobile),
 * hook không fetch.
 */
export function useScentingOutNotesInfinite(
    params: Omit<ListNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [...scentingLotKeys.outNotes(), 'infinite', params] as const,
        queryFn: ({ pageParam }) =>
            scentingLotApi.listOutNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useScentingOutNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: scentingLotKeys.outNoteDetail(id ?? ''),
        queryFn: () => scentingLotApi.getOutNote(id as string),
    })
}

export function useCreateScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateOutNoteRequest) =>
            scentingLotApi.createOutNote(req),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            toast.success(m.scl_toast_create_pxk())
        },
    })
}

export function useUpdateScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, req }: { id: string; req: UpdateOutNoteRequest }) =>
            scentingLotApi.updateOutNote(id, req),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            toast.success(m.scl_toast_update_pxk())
        },
    })
}

export function useApproveScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.approveOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            toast.success(m.scl_toast_approve_pxk())
        },
    })
}

/** Rollback PXK đã duyệt: BE sinh PNK điều chỉnh cộng lại tồn (M-17). */
export function useRollbackScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.rollbackOutNote(id),
        onSuccess: (res, id) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.inNotes() })
            qc.invalidateQueries({ queryKey: scentingLotKeys.lots() })
            toast.success(
                m.note_rollback_success({ code: res.data?.code ?? '' }),
            )
        },
    })
}

export function useRejectScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ id, reason }: { id: string; reason: string }) =>
            scentingLotApi.rejectOutNote(id, reason),
        onSuccess: (_data, { id }) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            toast.success(m.scl_toast_reject_pxk())
        },
    })
}

export function useCancelScentingOutNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.cancelOutNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.outNoteDetail(id),
            })
            qc.invalidateQueries({ queryKey: scentingLotKeys.outNotes() })
            toast.success(m.scl_toast_cancel_pxk())
        },
    })
}

/**
 * PXK lô lấy hương đã duyệt, lý do "xuất đi đóng gói" — nguồn chè hợp lệ cho PNK
 * sản phẩm. Dùng cho picker ở form tạo PNK.
 */
export function useScentingOutNotesForPackaging(
    search = '',
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: scentingLotKeys.outNotesForPackaging(search),
        queryFn: () =>
            scentingLotApi.listOutNotesForPackaging(search || undefined),
    })
}

// ============================================================================
// Phiếu lấy hàng (withdrawal note) queries
// ============================================================================

export function useScentingWithdrawalNotes(
    params: ListWithdrawalNotesParams = {},
    options?: { enabled?: boolean },
) {
    return useQuery({
        enabled: options?.enabled ?? true,
        queryKey: scentingLotKeys.withdrawalNoteList(params),
        queryFn: () => scentingLotApi.listWithdrawalNotes(params),
    })
}

/** Infinite variant cho mobile "Xem thêm" pattern. */
export function useScentingWithdrawalNotesInfinite(
    params: Omit<ListWithdrawalNotesParams, 'page'> = {},
    options?: { enabled?: boolean },
) {
    return useInfiniteQuery({
        enabled: options?.enabled ?? true,
        queryKey: [
            ...scentingLotKeys.withdrawalNotes(),
            'infinite',
            params,
        ] as const,
        queryFn: ({ pageParam }) =>
            scentingLotApi.listWithdrawalNotes({ ...params, page: pageParam }),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            const meta = lastPage.meta
            if (!meta) return undefined
            return meta.page < meta.total_pages ? meta.page + 1 : undefined
        },
    })
}

export function useScentingWithdrawalNote(id: string | undefined) {
    return useQuery({
        enabled: !!id,
        queryKey: scentingLotKeys.withdrawalNoteDetail(id ?? ''),
        queryFn: () => scentingLotApi.getWithdrawalNote(id as string),
    })
}

export function useCreateScentingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (req: CreateWithdrawalNoteRequest) =>
            scentingLotApi.createWithdrawalNote(req),
        onSuccess: () => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.withdrawalNotes(),
            })
            toast.success(m.scl_toast_create_plh())
        },
    })
}

export function useCancelScentingWithdrawalNote() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => scentingLotApi.cancelWithdrawalNote(id),
        onSuccess: (_data, id) => {
            qc.invalidateQueries({
                queryKey: scentingLotKeys.withdrawalNoteDetail(id),
            })
            qc.invalidateQueries({
                queryKey: scentingLotKeys.withdrawalNotes(),
            })
            toast.success(m.scl_toast_cancel_plh())
        },
    })
}
